import { UnitTypes } from 'src/Libs/types'
import { getChartColor } from './chartColors'
import type { CustomTrace } from '../../../chart-kit/shared/utils/chartModel'
import type { TraceKey } from './traceKey'

export * from './traceKey'

export interface TraceConf {
  custom: CustomTrace
  isCounterReading?: boolean
  hasForecast?: boolean
  isDeration?: boolean
  unit?: string
  types?: number[]
  sumKeys?: { key: string; operand: 'subtract' | 'add' }[]
}

export const traceConf: Record<TraceKey, TraceConf> = {
  ThreeSixtySavings: {
    custom: {
      key: 'ThreeSixtySavings',
      color: getChartColor('ThreeSixtySavings'),
      opacity: 0.25,
    },
    unit: UnitTypes.percentage,
    isCounterReading: true,
    hasForecast: true,
  },
  Temperature: {
    custom: {
      key: 'Temperature',
      color: getChartColor('Temperature'),
    },
    unit: UnitTypes.celsius,
    // hasForecast: true,
  },
  //#region History
  BatPowerIn: {
    custom: {
      key: 'BatPowerIn',
      color: getChartColor('BatPowerIn'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  BatPowerOut: {
    custom: {
      key: 'BatPowerOut',
      color: getChartColor('BatPowerOut'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  NetIn: {
    custom: {
      key: 'NetIn',
      color: getChartColor('NetIn'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  NetOut: {
    custom: {
      key: 'NetOut',
      color: getChartColor('NetOut'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  NetOutWithoutThreeSixtySavings: {
    custom: {
      key: 'NetOut',
      color: getChartColor('NetOutWithoutThreeSixtySavings'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  ASOC: {
    custom: {
      key: 'ASOC',
      color: getChartColor('ASOC'),
      opacity: 0.5,
      type: 'area',
    },
    unit: '%',
    isCounterReading: false,
    hasForecast: true,
  },
  DC_PowerL1: {
    custom: {
      key: 'DC_PowerL1',
      color: getChartColor('DC_PowerL1'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  DC_PowerL2: {
    custom: {
      key: 'DC_PowerL2',
      color: getChartColor('DC_PowerL2'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  DC_PowerL3: {
    custom: {
      key: 'DC_PowerL3',
      color: getChartColor('DC_PowerL3'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  /*  DC_PowerL1 + DC_PowerL2 + DC_PowerL3 */
  DC_PowerSolar: {
    custom: {
      key: 'DC_PowerSolar',
      color: getChartColor('DC_PowerSolar'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  /* DC_PowerSolar + EXT Powermeter (production type: 2 & 3) */
  DC_PowerTotal: {
    custom: {
      key: 'DC_PowerTotal',
      color: getChartColor('DC_PowerTotal'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  ForecastPower: {
    custom: {
      key: 'ForecastPower',
      color: getChartColor('ForecastPower'),
      thickness: 1.5,
      dash: 'dash',
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: false,
  },
  WeatherBreakthrough: {
    custom: {
      key: 'WeatherBreakthrough',
      color: getChartColor('WeatherBreakthrough'),
      thickness: 1.5,
      dash: 'dash',
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: false,
  },
  /*  House Consumption + Ext Powermeters Consumption (type: 3 & 4) + Wallboxes */
  ConsumptionTotal: {
    custom: {
      key: 'ConsumptionTotal',
      color: getChartColor('ConsumptionTotal'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  /* ConsumptionTotal - Ext Powermeters Consumption (type: 3 & 4) - Wallboxes */
  ConsumptionHouse: {
    // custom: { color: '#CC3333' },
    custom: {
      key: 'ConsumptionHouse',
      color: getChartColor('ConsumptionHouse'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  /* DC_PowerTotal (DC_PowerSolar + EXT Powermeter (type: 2 & 3)) - BatPowerIn - NetIn */
  DirectConsumption: {
    custom: { color: getChartColor('DirectConsumption'), key: 'DirectConsumption', summaryUnit: UnitTypes.wattHour },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: false,
  },
  /* DirectConsumption (DC_PowerTotal (DC_PowerSolar + EXT Powermeter (type: 2 & 3)) - BatPowerIn - NetIn) + BatPowerOut + NetOut */
  DirectConsumptionProduction: {
    custom: { color: getChartColor('DirectConsumptionProduction'), key: 'DirectConsumptionProduction', summaryUnit: UnitTypes.wattHour },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: false,
  },
  /*  DC_PowerTotal + BatPowerOut - BatPowerIn - NetIn */
  GreenConsumption: {
    custom: { color: getChartColor('GreenConsumption'), key: 'GreenConsumption', summaryUnit: UnitTypes.wattHour },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  /* ConsumptionTotal + unitDeration */
  Deration: {
    custom: { color: getChartColor('Deration'), dash: 'dash', key: 'Deration', summaryUnit: UnitTypes.wattHour },
    isDeration: true,
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  /* Consumption - NetOut */
  SelfProduction: {
    custom: { color: getChartColor('SelfProduction'), key: 'SelfProduction', summaryUnit: UnitTypes.wattHour },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  /* same as SelfProduction, difference is that W/WH unit in pie summary should not be displayed AND name is "Autarkie" instead of "Eigenstrom" */
  Autarky: {
    custom: {
      key: 'Autarky',
      color: getChartColor('Autarky'),
      // reduce opacity by setting fillcolor for spline chart and opacity for bar chart
      // fillcolor: 'rgba(154, 193, 95, 0.25)',
      opacity: 0.25,
    },
    unit: '%',
    isCounterReading: true,
    hasForecast: true,
  },
  //#endregion

  //#region Wallbox
  Wallbox_energyAll: {
    custom: {
      key: 'Wallbox_energyAll',
      color: getChartColor('Wallbox_energyAll'),
      ids: [],
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  Wallbox_energySolar: {
    custom: {
      key: 'Wallbox_energySolar',
      color: getChartColor('Wallbox_energySolar'),
      ids: [],
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  Wallbox_energyNetOut: {
    custom: {
      key: 'Wallbox_energyNetOut',
      color: getChartColor('Wallbox_energyNetOut'),
      ids: [],
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  Wallbox_energyDischarged: {
    custom: {
      key: 'Wallbox_energyDischarged',
      color: getChartColor('Wallbox_energyDischarged'),
      ids: [],
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  //#endregion

  //#region Powermeter
  Powermeter_NetIn: {
    custom: { key: 'Powermeter_NetIn', color: getChartColor('Powermeter_NetIn'), ids: [], summaryUnit: UnitTypes.wattHour },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: false,
  },
  Powermeter_NetOut: {
    custom: { key: 'Powermeter_NetOut', color: getChartColor('Powermeter_NetOut'), ids: [], summaryUnit: UnitTypes.wattHour },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: false,
  },
  /* basically NetIn but additional filter TYPE 2 & 3 */
  Powermeter_production: {
    custom: { key: 'Powermeter_production', color: getChartColor('Powermeter_production'), ids: [], summaryUnit: UnitTypes.wattHour },
    types: [2, 3],
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  /* basically NetOut but additional filter TYPE 3 & 4 */
  Powermeter_consumption: {
    custom: { key: 'Powermeter_consumption', color: getChartColor('Powermeter_consumption'), ids: [], summaryUnit: UnitTypes.wattHour },
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: true,
  },
  /* basically NetIn but additional filter TYPE 9 */
  Powermeter_NetIn_alien: {
    custom: {
      key: 'Powermeter_NetIn_alien',
      color: getChartColor('Powermeter_NetIn_alien'),
      thickness: 1.5,
      dash: 'dot',
      ids: [],
      summaryUnit: UnitTypes.wattHour,
    },
    types: [9],
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: false,
  },
  /* basically NetOut but additional filter TYPE 9 */
  Powermeter_NetOut_alien: {
    custom: {
      key: 'Powermeter_NetOut_alien',
      color: getChartColor('Powermeter_NetOut_alien'),
      thickness: 1.5,
      dash: 'dot',
      ids: [],
      summaryUnit: UnitTypes.wattHour,
    },
    types: [9],
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: false,
  },
  /* basically NetIn but additional filter TYPE 200 */
  Powermeter_NetIn_enera: {
    custom: {
      key: 'Powermeter_NetIn_enera',
      color: getChartColor('Powermeter_NetIn_enera'),
      ids: [],
      summaryUnit: UnitTypes.wattHour,
    },
    types: [200],
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: false,
  },
  /* basically NetIn but additional filter TYPE 10 */
  Powermeter_NetIn_bypass: {
    custom: {
      key: 'Powermeter_NetIn_bypass',
      color: getChartColor('Powermeter_NetIn_bypass'),
      ids: [],
      summaryUnit: UnitTypes.wattHour,
    },
    types: [10],
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: false,
  },
  Powermeter_NetIn_bypass_noCharge: {
    custom: {
      key: 'Powermeter_NetIn_bypass_noCharge',
      color: getChartColor('Powermeter_NetIn_bypass_noCharge'),
      ids: [],
      summaryUnit: UnitTypes.wattHour,
    },
    types: [11],
    unit: UnitTypes.watt,
    isCounterReading: true,
    hasForecast: false,
  },
  //#endregion
  Custom_Sum: {
    custom: {
      key: 'Custom_Sum',
      color: getChartColor('Custom_Sum'),
      summaryUnit: UnitTypes.wattHour,
    },
    unit: UnitTypes.watt,
    hasForecast: false,
    sumKeys: [],
  },
}
